<template>
    <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
            <span>{{ $t('Alatunnisteen tekstit') }}</span>
            <v-chip small outlined>{{ items.length }} {{ $t('tekstiä') }}</v-chip>
        </v-card-title>

        <v-card-subtitle>
            <v-btn small color="primary" @click="openAddDialog" :disabled="loading">
                <v-icon left small>{{ icons.mdiPlus }}</v-icon>
                {{ $t('Lisää teksti') }}
            </v-btn>
        </v-card-subtitle>

        <v-divider></v-divider>

        <v-card-text>
            <!-- Empty State -->
            <div v-if="items.length === 0" class="text-center py-8">
                <v-icon size="64" color="grey lighten-2">{{ icons.mdiTextBox }}</v-icon>
                <p class="text-h6 grey--text mt-4">{{ $t('Ei alatunnisteen tekstejä') }}</p>
                <p class="grey--text">{{ $t('Lisää tekstejä painamalla yllä olevaa painiketta') }}</p>
            </div>

            <!-- Footer Texts List -->
            <draggable v-else v-model="localItems" :disabled="loading" handle=".drag-handle" @end="handleDragEnd"
                ghost-class="ghost-card">
                <v-card v-for="item in localItems" :key="item.id" class="mb-3" outlined>
                    <v-card-text class="d-flex align-center">
                        <!-- Drag Handle -->
                        <v-icon class="drag-handle mr-3" :class="{ 'grey--text': loading }">
                            {{ icons.mdiDragVertical }}
                        </v-icon>

                        <!-- Text Content -->
                        <div class="flex-grow-1">
                            <div class="font-weight-medium">{{ item.text }}</div>
                        </div>

                        <!-- Actions -->
                        <v-btn small color="error" @click="handleRemove(item.id)" :disabled="loading">
                            <v-icon small>{{ icons.mdiDelete }}</v-icon>
                        </v-btn>
                    </v-card-text>
                </v-card>
            </draggable>
        </v-card-text>

        <!-- Add Text Dialog -->
        <v-dialog v-model="addDialog" max-width="500px">
            <v-card>
                <v-card-title>{{ $t('Lisää alatunnisteen teksti') }}</v-card-title>
                <v-card-text>
                    <v-textarea v-model="newText" :label="$t('Teksti')" rows="3" counter="500"
                        :rules="[rules.required, rules.maxLength]" outlined></v-textarea>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="addDialog = false">{{ $t('Peruuta') }}</v-btn>
                    <v-btn color="primary" @click="handleAdd" :disabled="!isValidText">{{ $t('Lisää') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-card>
</template>

<script>
import { ref, watch, computed } from '@vue/composition-api'
import draggable from 'vuedraggable'
import {
    mdiPlus,
    mdiTextBox,
    mdiDragVertical,
    mdiDelete
} from '@mdi/js'
import { useTranslations } from '@/@core/libs/i18n';
import { i18n } from '@/plugins/i18n';

export default {
    name: 'FooterTextsList',
    components: {
        draggable
    },
    props: {
        items: {
            type: Array,
            default: () => []
        },
        screenId: {
            type: [String, Number],
            required: true
        },
        loading: {
            type: Boolean,
            default: false
        }
    },
    setup(props, { emit }) {
        const localItems = ref([...props.items])
        const addDialog = ref(false)
        const newText = ref('')


        // Watch for external changes to items
        watch(() => props.items, (newItems) => {
            localItems.value = [...newItems]
        }, { deep: true })

        const rules = {
            required: v => !!v || i18n.t('Teksti on pakollinen'),
            maxLength: v => (v && v.length <= 500) || i18n.t('Teksti saa olla enintään 500 merkkiä')
        }

        const isValidText = computed(() => {
            return newText.value && newText.value.length > 0 && newText.value.length <= 500
        })

        const openAddDialog = () => {
            newText.value = ''
            addDialog.value = true
        }

        const handleAdd = () => {
            if (isValidText.value) {
                emit('add', newText.value)
                addDialog.value = false
                newText.value = ''
            }
        }

        const handleRemove = (textId) => {
            emit('remove', textId)
        }

        const handleDragEnd = () => {
            // Emit reorder event with new order
            emit('reorder', localItems.value)
        }

        return {
            localItems,
            addDialog,
            newText,
            rules,
            isValidText,
            openAddDialog,
            handleAdd,
            handleRemove,
            handleDragEnd,
            icons: {
                mdiPlus,
                mdiTextBox,
                mdiDragVertical,
                mdiDelete
            }
        }
    }
}
</script>

<style scoped>
.drag-handle {
    cursor: move;
}

.drag-handle.grey--text {
    cursor: not-allowed;
}

.ghost-card {
    opacity: 0.5;
    background-color: #f5f5f5;
}
</style>
